/**
 * The catalog's copy in IndexedDB: one record, read once at boot and written
 * after every change. Storage that is unavailable (private mode, blocked site
 * data), slow to answer, or holding something that is not a catalog reads as no
 * copy, and a failed write is only logged — the app then loads from the network
 * as on a first visit.
 */
import type { Catalog } from "@auger/core"

const DB_NAME = "auger-catalog"
const STORE = "catalog"
const KEY = "snapshot"
// Bump when the persisted shape changes: an older copy is then ignored.
const FORMAT = 1
// The router guard awaits the read. Safari has had `indexedDB.open` requests
// that never settle; past this the app loads from the network instead.
export const READ_TIMEOUT_MS = 1_000

interface Persisted {
  format: number
  catalog: Catalog
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1)
    req.onupgradeneeded = () => req.result.createObjectStore(STORE)
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

function request<T>(mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest<T>) {
  return openDb().then((db) =>
    new Promise<T>((resolve, reject) => {
      const req = run(db.transaction(STORE, mode).objectStore(STORE))
      req.onsuccess = () => resolve(req.result)
      req.onerror = () => reject(req.error)
    }).finally(() => db.close())
  )
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new Error(`no answer from IndexedDB in ${ms} ms`)), ms)
  })
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer))
}

/** Enough of a catalog's shape for `catalog.register` to take it. */
function isCatalog(value: unknown): value is Catalog {
  const c = value as Catalog | null | undefined
  return (
    !!c &&
    Array.isArray(c.schemas) &&
    Array.isArray(c.types) &&
    Array.isArray(c.users) &&
    typeof c.status?.schema_count === "number"
  )
}

/** The persisted catalog, or null when there is none (or storage is unavailable). */
export async function readCatalog(): Promise<Catalog | null> {
  try {
    const stored = await withTimeout(
      request<Persisted | undefined>("readonly", (s) => s.get(KEY)),
      READ_TIMEOUT_MS
    )
    return stored?.format === FORMAT && isCatalog(stored.catalog) ? stored.catalog : null
  } catch (err) {
    console.warn("[catalog] could not read the persisted catalog", err)
    return null
  }
}

// The app does not wait for a clear or a write. Each returns a promise that
// resolves once the request has settled, and never rejects (a failure is only
// logged), so a caller that must see the result, such as a test, can await it.

/** Drop the persisted copy — one the catalog could not register. */
export function clearCatalog(): Promise<void> {
  return request("readwrite", (s) => s.delete(KEY)).then(
    () => {},
    (err) => {
      console.warn("[catalog] could not clear the persisted catalog", err)
    }
  )
}

export function writeCatalog(catalog: Catalog): Promise<void> {
  const stored: Persisted = { format: FORMAT, catalog }
  return request("readwrite", (s) => s.put(stored, KEY)).then(
    () => {},
    (err) => {
      console.warn("[catalog] could not persist the catalog", err)
    }
  )
}
